import { Box, Button } from '@mui/material';
import { useRouter } from 'next/navigation';

import { TAGSvgIcon } from '@shared-components-web';
import NeedHelp from '@src/components/NeedHelp';
import { RECAPTCHA_ACTION_NAME } from '@src/constants';
import useScrollToTop from '@src/hooks/useScrollToTop';
import { getLogger } from '@src/lib/logging/logger';
import { useClientInformationStore, useRouteHandlerStore } from '@src/store';

type FormCTAsProps = {
	formId: string;
	setLoading?: (loading: boolean) => void;
	recaptchaSiteKey?: string;
};

export default function FormCTAs({ formId, setLoading, recaptchaSiteKey }: Readonly<FormCTAsProps>) {
	const router = useRouter();
	const { routeProgress, handleBackButton, reviewStepActive } = useRouteHandlerStore((store) => store);
	const { clientId } = useClientInformationStore((store) => store);
	const { previousRoutes } = routeProgress();
	const scrollToTop = useScrollToTop();
	const logger = getLogger('client');

	const verifyRecaptcha = async (e: React.MouseEvent<HTMLButtonElement>) => {
		const submitForm = () => {
			const formEl = document.getElementById(formId) as HTMLFormElement | null;
			formEl?.dispatchEvent(new Event('submit', { bubbles: true }));
		};
		const bypass = (category: string, reason: string) => {
			logger.info({
				name: '',
				category,
				value: `client: ${clientId} , reason: ${reason}`,
			});
			submitForm();
		};

		scrollToTop();
		if (!reviewStepActive()) return;
		e.preventDefault();

		if (!recaptchaSiteKey?.trim()) {
			bypass('reCAPTCHA Bypass - Missing Site Key', 'missing site key');
			return;
		}
		const googleRecaptchaEnterprise = (globalThis as any).grecaptcha?.enterprise;
		if (!googleRecaptchaEnterprise) {
			bypass('reCAPTCHA Bypass - grecaptcha Not Loaded', 'grecaptcha not loaded');
			return;
		}
		try {
			await googleRecaptchaEnterprise.ready(async () => {
				const token = await googleRecaptchaEnterprise.execute(recaptchaSiteKey, {
					action: RECAPTCHA_ACTION_NAME,
				});
				const VERIFY_RECAPTCHA_ENDPOINT = `${process.env.APP_PATH}/api/recaptcha-assess`;
				const resp = await fetch(VERIFY_RECAPTCHA_ENDPOINT, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ token, action: RECAPTCHA_ACTION_NAME }),
				});
				const data = await resp.json();
				logger.info({
					name: '',
					category: 'reCAPTCHA Assess - Before Client Appointment Confirmation',
					value: `client: ${clientId} , action : ${RECAPTCHA_ACTION_NAME} , data : ${JSON.stringify(data)}`,
				});
				submitForm();
			});
		} catch (err) {
			logger.error({
				name: '',
				category: 'reCAPTCHA exception Error',
				value: `client: ${clientId} , action : ${RECAPTCHA_ACTION_NAME} , error : ${JSON.stringify(err)}`,
			});
			submitForm();
		}
	};
	return (
		<Box
			display="flex"
			justifyContent="center"
			alignItems="center"
			bgcolor="backgrounds.white"
			width={'100%'}
			mt={2}
			position="sticky"
			bottom={0}
			sx={{
				boxShadow: '0 -1px 4px rgb(0 0 0 / 20%)',
				pt: 2,
				pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',
			}}
			zIndex={(theme) => theme.zIndex.appBar}
		>
			<NeedHelp />
			{previousRoutes.length ? (
				<Button
					variant="secondaryDefault"
					size="M"
					startIcon={<TAGSvgIcon icon={'ChevronLeftFunc'} size={20} />}
					onClick={() =>
						handleBackButton((route: string) => {
							if (setLoading) setLoading(true);
							scrollToTop();
							router.replace(route);
						})
					}
					sx={{ marginRight: 1 }}
				>
					Back
				</Button>
			) : null}
			<Button
				type="submit"
				form={formId}
				variant="primaryDefault"
				size="M"
				onClick={verifyRecaptcha}
				sx={{
					marginLeft: previousRoutes.length ? 1 : 0,
					'&:hover:not(:focus)': {
						bgcolor: 'secondary.main',
					},
				}}
			>
				{reviewStepActive() ? 'Confirm' : 'Continue'}
			</Button>
		</Box>
	);
}
